import { Injectable, inject, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { forkJoin, Observable, map } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { Customer } from "../../../../../models/api/asset/customer";
import { GateFault } from "../../../../../models/api/fault/gateFault";
import { AttributeResponse, TelemetryResponse } from "../../../../../models/shared/telemetry";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { GateFaultService } from "../../../../../services/api/gate-fault/gate-fault.service";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";
import { GateFaultUtilsService } from "../../../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { DOCUMENT } from "@angular/common";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../../../services/websocket/gate-fault/gate-fault-websocket.service";
import {
  AttributesSubscriptionHandle,
  AttributesWebsocketService,
} from "../../../../../services/websocket/attributes-websocket/attributes-websocket.service";
import { SeverityCounts } from "../../../../../models/ui/severityCounts";

const TELEMETRY_KEYS: string[] = [
  "numberOfGateSystems",
  "readyGateSystems",
  "criticalGateSystems",
  "majorGateSystems",
  "minorGateSystems",
];
@Injectable()
export class CustomerHomeService {
  // Services needed:
  private gateFaultService = inject(GateFaultService);
  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);
  private energyToolUtils = inject(EnergyToolUtilsService);
  private translate = inject(TranslateService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);
  private attributesWebsocket = inject(AttributesWebsocketService);
  private gateFaultSubscription?: GateFaultSubscriptionHandle;
  private attributesSubscription?: AttributesSubscriptionHandle;

  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  // State variables:
  private readonly _severityCounts = signal<SeverityCounts | null>(null);
  public readonly severityCounts = this._severityCounts.asReadonly();

  private readonly _gateFaultState = signal<GateFault[] | null>(null);
  readonly gateFaultState = this._gateFaultState.asReadonly();

  private readonly _lineChartEnergyLoss = signal<any>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _energyLossYearly = signal<number | null>(null);
  readonly energyLossYearly = this._energyLossYearly.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  /**
   * Initializes and loads the initial state for the customer home view.
   *
   * This method fetches and processes various data related to the customer,
   * including attributes, gate faults, energy loss, and gate systems. It then
   * updates the state variables accordingly and initializes real-time updates
   * for gate faults and attributes.
   *
   * @param customerId - The ID of the customer for whom the initial state is to be loaded.
   */

  public loadInitialState(customerId: string) {
    this.resetStates();
    const gateFaults$ = this.fetchGateFaults();
    const attributes$ = this.fetchAttributes(customerId);
    const customer$ = this.fetchCustomer(customerId);
    const energyLoss$ = this.fetchEnergyLoss(customerId);
    const customerGateSystems$ = this.fetchCustomerGateSystems(customerId);

    forkJoin({
      attributes: attributes$,
      gateFaults: gateFaults$,
      customer: customer$,
      energyLoss: energyLoss$,
      customerGateSystems: customerGateSystems$,
    }).subscribe((res) => {
      const attributes = res?.attributes;
      const gateFaults = res?.gateFaults;
      const energyLoss = res?.energyLoss;
      const customerGateSystems = res?.customerGateSystems;

      const customer: Customer = res?.customer;

      const readyGateSystems = attributes?.readyGateSystems?.value;
      const majorGateSystems = attributes?.majorGateSystems?.value;
      const minorGateSystems = attributes?.minorGateSystems?.value;
      const criticalGateSystems = attributes?.criticalGateSystems?.value;
      const numberOfGateSystems = attributes?.numberOfGateSystems?.value;

      this._severityCounts.set({
        OK: readyGateSystems,
        MAJOR: majorGateSystems,
        CRITICAL: criticalGateSystems,
        MINOR: minorGateSystems,
        TOTAL: numberOfGateSystems,
      });

      if (isNotNullOrUndefined(customer.expectedEnergyLossPerDay)) {
        this._expectedEnergyLossPerDay.set(customer.expectedEnergyLossPerDay);
      }

      this.extractEnergyLossSum(energyLoss);
      this.extractEnergyLossLineChart(energyLoss, customer);

      this._gateFaultState.set(gateFaults);

      this.initRealtimeGateFaults(customerGateSystems);
      this.initRealtimeAttributes(customerId);
    });
  }

  /**
   * Destroys the real-time WebSocket connections.
   * This is used when the user navigates away from the CustomerHome page.
   */
  public destroy() {
    this.resetStates();
    this.gateFaultSubscription?.close();
    this.attributesSubscription?.close();
  }

  /**
   * Resets all state signals to null.
   * This is used to reset the component's state when the user navigates away.
   */
  private resetStates() {
    this._severityCounts.set(null);
    this._gateFaultState.set(null);
    this._lineChartEnergyLoss.set(null);
    this._energyLossYearly.set(null);
    this._expectedEnergyLossPerDay.set(null);
  }

  /**
   * Initializes real-time gate faults WebSocket connection.
   * @param gateSystemIds gate system IDs to listen for
   */
  private initRealtimeGateFaults(gateSystemIds: string[]) {
    this.gateFaultSubscription?.close();
    const subscription = this.gateFaultWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: gateSystemIds,
      lang: this.localStorage?.getItem("lang"),
    });
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          this.gateFaultUtils.handleRealtimeGateFault(
            incomingData.data,
            this._gateFaultState.update.bind(this._gateFaultState)
          );
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Initializes WebSocket connection for customer attributes.
   * @param customerId ID of customer to listen for attributes.
   */
  private initRealtimeAttributes(customerId: string) {
    this.attributesSubscription?.close();
    const subscription = this.attributesWebsocket.register({
      entityType: ASSET_TYPES.CUSTOMER,
      entityId: [customerId],
      keys: TELEMETRY_KEYS,
    });
    this.attributesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        this.handleRealtimeAttributes(incomingData.data);
      },
      error: () => undefined,
    });
  }

  /**
   * Updates the real-time states of the customer based on the incoming attribute.
   * @param incomingAttribute The incoming attribute to apply to the real-time states.
   */
  private handleRealtimeAttributes(incomingAttribute: AttributeResponse) {
    // Extract and convert all present data points
    const updates: Record<string, number> = {};

    for (const key in incomingAttribute) {
      const entry = incomingAttribute[key];
      if (entry && entry.value !== undefined) {
        updates[key] = entry.value as number;
      }
    }

    // If no valid data points found, return early
    if (Object.keys(updates).length === 0) {
      return;
    }

    Object.entries(updates).forEach(([key, value]) => {
      switch (key) {
        case "numberOfGateSystems":
          this._severityCounts.update((severityCounts) => {
            const newSeverityCounts = severityCounts ? { ...severityCounts, TOTAL: Number(value) } : null;
            return newSeverityCounts;
          });
          break;
        case "readyGateSystems":
          this._severityCounts.update((severityCounts) => {
            const newSeverityCounts = severityCounts ? { ...severityCounts, OK: Number(value) } : null;
            return newSeverityCounts;
          });
          break;
        case "criticalGateSystems":
          this._severityCounts.update((severityCounts) => {
            const newSeverityCounts = severityCounts ? { ...severityCounts, CRITICAL: Number(value) } : null;
            return newSeverityCounts;
          });
          break;
        case "majorGateSystems":
          this._severityCounts.update((severityCounts) => {
            const newSeverityCounts = severityCounts ? { ...severityCounts, MAJOR: Number(value) } : null;
            return newSeverityCounts;
          });
          break;
        case "minorGateSystems":
          this._severityCounts.update((severityCounts) => {
            const newSeverityCounts = severityCounts ? { ...severityCounts, MINOR: Number(value) } : null;
            return newSeverityCounts;
          });
          break;
        default:
          break;
      }
    });
  }

  /**
   * Updates the yearly energy loss state with the total energy loss for the current year.
   * If the total yearly energy loss is undefined, the state is not updated.
   * @param energyLoss telemetry response containing energy loss data
   */
  private extractEnergyLossSum(energyLoss: TelemetryResponse | null) {
    if (energyLoss && energyLoss.energy_loss) {
      const yearlyLoss = this.energyToolUtils.getTimeWindowEnergyLoss(energyLoss.energy_loss, "this_year");
      if (yearlyLoss !== undefined) {
        this._energyLossYearly.set(yearlyLoss);
      } else {
        this._energyLossYearly.set(0);
      }
    }
  }

  /**
   * Extracts line chart data for yearly energy loss from telemetry response.
   * @param energyLoss telemetry response containing energy loss data
   * @param customer customer object containing expected yearly energy loss
   */
  private extractEnergyLossLineChart(energyLoss: TelemetryResponse, customer: Customer) {
    const chartData = this.energyToolUtils.convertTimeseriesToChartData(
      energyLoss.energy_loss,
      "day",
      "year",
      false,
      DateTime.now().toMillis(),
      true
    );
    if (customer.expectedEnergyLossPerYear === undefined) {
      return;
    }
    const expectedChartData = this.energyToolUtils.createExpectedChart(customer.expectedEnergyLossPerYear);
    this._lineChartEnergyLoss.set([
      {
        label: this.translate.instant("energy_loss_yearly_chart.line_label"),
        data: chartData,
        borderColor: "#ffffff",
      },
      {
        label: this.translate.instant("energy_loss_yearly_chart.expected_label"),
        data: expectedChartData,
        borderColor: "#ff6347",
      },
    ]);
  }
  /**
   * Fetches the list of gate system IDs associated with a specific customer.
   *
   * @param customerId - The ID of the customer whose gate systems are to be fetched.
   * @returns An observable that emits an array of gate system IDs.
   */
  private fetchCustomerGateSystems(customerId: string): Observable<string[]> {
    return this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM, customerId, ASSET_TYPES.CUSTOMER).pipe(
      map((res) => {
        const gateSystemIds: string[] = [];
        res.forEach((gateSystem) => {
          if (gateSystem && gateSystem.id) {
            gateSystemIds.push(gateSystem.id);
          }
        });
        return gateSystemIds;
      })
    );
  }

  /**
   * Fetches energy loss data for the given customer for the current year.
   * @param customerId - The ID of the customer to fetch energy loss data for.
   * @returns An observable of the energy loss data.
   */
  private fetchEnergyLoss(customerId: string) {
    const now = DateTime.now();
    return this.telemetryService.getTimeseries(
      customerId,
      ASSET_TYPES.CUSTOMER,
      "energy_loss",
      now.startOf("year").toMillis(),
      now.toMillis()
    );
  }

  /**
   * Fetches a customer based on the given customer ID.
   * @param customerId - The ID of the customer to fetch.
   * @returns An observable of the customer object.
   */
  private fetchCustomer(customerId: string) {
    return this.assetService.getAssetDetails(customerId, ASSET_TYPES.CUSTOMER).pipe(
      map((res) => {
        return res as Customer;
      })
    );
  }

  /**
   * Fetches all gate faults.
   * @returns An observable of all gate faults.
   */
  private fetchGateFaults() {
    return this.gateFaultService.getGateFaults(undefined, undefined, true);
  }

  /**
   * Fetches customer attributes.
   * @param customerId - The ID of the customer to fetch attributes for.
   * @returns An observable of the customer attributes.
   */
  private fetchAttributes(customerId: string) {
    return this.telemetryService.getAttributes(customerId, ASSET_TYPES.CUSTOMER, TELEMETRY_KEYS.join(","));
  }
}
